"use client";
import React, {
  useState,
  useRef,
  useState as useReactState,
  useEffect,
} from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CalcInputValues from "../ui/CalcInputValues";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";
import { SchemeOptional } from "@/type/commonTypes";

import { allCagrCalculation } from "@/utils/allCalculators";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { CategoryFundsByAssetClass } from "@/type/schemeType";
import Image from "next/image";
import { openInvestmentModal } from "@/redux/features/InvestmentModal/investmentModalSlice";
import { useDispatch } from "react-redux";
import { ROOT_URL } from "@/utils/gloableVariables";

interface CagrResult {
  cagr: number;
  gain: number;
  initial: number;
  final: number;
  years: number;
}

export default function CagrCalc() {
  const [isOpen, setIsOpen] = useState(false);

  const [investmentAmount, setInvestmentAmount] = useState(1000000);
  const [finalAmount, setFinalAmount] = useState(10000000);
  const [years, setYears] = useState(5);

  const dispatch = useDispatch();

  const [fundsData, setFundsData] =
    useReactState<CategoryFundsByAssetClass | null>(null);
  const [activeTab, setActiveTab] = useState<"Equity" | "Hybrid" | "Debt">(
    "Equity",
  );

  const [calculation, setCalculation] = useState<CagrResult>();
  const isTab = useIsMobile(1025);

  useEffect(() => {
    const result = allCagrCalculation(investmentAmount, finalAmount, years);
    setCalculation(result);
  }, [investmentAmount, finalAmount, years]);

  useEffect(() => {
    async function fetchTopFunds() {
      try {
        const res = await fetch("/api/cagrTopFunds");
        const data = await res.json();
        if (data.success) {
          setFundsData(data.data);
        }
      } catch (err) {
        console.error(`${err} | Something Went Wrong`);
      }
    }

    fetchTopFunds();
  }, []);

  const handleRightPart = () => {
    if (!isTab) return;
    setIsOpen(true);
  };

  return (
    <>
      {/* Hero Heading */}
      <section className="section px-3 sm:px-6 lg:px-3 mb-12 sm:mb-100">
        {/* Floating Calculator */}
        <div className="relative w-full mx-auto xs:max-w-[90%] lg:max-w-302.5 xs:bg-white xs:rounded-xl xs:shadow-lg sm:overflow-hidden">
          <div className="flex flex-col lg:flex-row gap-6 items-center w-full bg-white text-black rounded-xl shadow-lg p-4 pb-6 xl:p-6">
            {/* Inputs */}
            <div className="w-full sm:p-3 lg:w-[46%] shrink-0">
              <h2 className="xs:text-lg sm:text-2xl lg:text-2.5xl font-semibold text-center text-primaryGreen mb-6 sm:mb-8">
                Calculate Your CAGR
              </h2>

              <div className="space-y-8 sm:space-y-12">
                <CalcInputValues
                  label="Initial Investment"
                  min={5000}
                  max={500000000}
                  unit="₹"
                  unitPosition="left"
                  amountValue={investmentAmount}
                  onChange={setInvestmentAmount}
                  showFloatingValue
                />

                <CalcInputValues
                  label="Final Value Cost"
                  min={5000}
                  max={1000000000}
                  unit="₹"
                  unitPosition="left"
                  amountValue={finalAmount}
                  onChange={setFinalAmount}
                  showFloatingValue
                />

                <CalcInputValues
                  label="Duration of Investment"
                  min={1}
                  max={50}
                  unit="Years"
                  amountValue={years}
                  onChange={setYears}
                />
              </div>

              <button
                onClick={handleRightPart}
                className="block lg:hidden mt-4 w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5"
              >
                Calculate
              </button>
            </div>

            {!isTab && (
              <RightPart
                className="hidden lg:block"
                calculation={calculation!}
              />
            )}
          </div>
        </div>
      </section>

      {/* Suggested Funds to Invest as Equity, Hybrid, Dept */}
      <section className="section mb-half-px sm:mb-100 relative">
        <h2 className="text-1.5xl xs:text-2xl md:text-3xl font-semibold mb-5 text-center">
          Explore Top Mutual Funds with High CAGR Returns
        </h2>
        {/* Tabs */}
        <div className="flex items-center justify-center gap-2.5 sm:gap-6 md:gap-10 mb-10 border border-[#c6d3ea] py-1 px-1.5 rounded-full w-fit mx-auto">
          {(["Equity", "Hybrid", "Debt"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 xs:px-8 sm:px-10 py-2 font-semibold text-2sm rounded-full transition-all duration-300 ${activeTab === tab ? "bg-primaryGreen border text-white border-primaryGreen" : "bg-white text-[#20202080] hover:text-primaryGreen hover:bg-[#047f791a]"}`}
            >
              {tab}
            </button>
          ))}
        </div>
        {/* Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-4 2md:gap-9">
          {fundsData && fundsData[activeTab]?.length > 0 ? (
            fundsData[activeTab].map((fund, idx) => {
              const formPlayload: SchemeOptional = {
                scheme_code: fund.scheme_code,
                scheme_name: fund.scheme_name,
                amc_code: fund.amc_code,
                sip_allowed: fund.sip_allowed,
                lump_allowed: fund.lump_allowed,
                minimum_sip_invest: fund.minimum_sip_invest,
                minimum_investment: fund.minimum_investment,
              };
              return (
                <div
                  key={fund.return_id}
                  className="group bg-transparent rounded-2xl"
                >
                  <div
                    className={`bg-white border-2 border-b-0 p-4 2md:p-5 border-[#D9D9D9] rounded-t-2xl transition-all duration-300 overflow-hidden flex flex-col ${idx === 1 ? "relative 2lg:scale-110 2lg:group-hover:scale-[1.11]" : "2lg:group-hover:scale-[1.01]"} group-hover:border-primaryGreen group-hover:bg-[linear-gradient(178.22deg,#FFFFFF_1.51%,#DAFFFD_98.57%)]`}
                  >
                    {idx === 1 && (
                      <PremiumSvgTag className="absolute -top-px right-3 w-4.5 max-h-7" />
                    )}
                    <a
                      href={`${ROOT_URL}mutual-fund/${fund.url}/mso${fund.scheme_id}`}
                      className="group border-b border-[#00000033] pb-5 mb-4 flex gap-3 items-center text-left"
                    >
                      <Image
                        src={`${ROOT_URL}img/AMCsLogo/${fund.amc_code}.png`}
                        alt={fund.scheme_name}
                        width={40}
                        height={40}
                        className="overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full"
                      />

                      <p className="font-medium text-primaryBlack text-2sm xs:text-base 2lg:text-lg line-clamp-2 transition group-hover:text-primaryGreen">
                        {fund.scheme_name}
                      </p>
                    </a>

                    <div className="text-left flex-1">
                      <div className="flex items-center justify-between gap-3 text-sm xs:text-2sm 2lg:text-base">
                        <p className="text-primaryBlack">3Y SIP Returns</p>
                        <p className="text-primaryGreen font-semibold">
                          {fund.three_year}%
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-3 text-sm xs:text-2sm 2lg:text-base text-primaryBlack mt-3 sm:mt-4">
                        <p>Minimum SIP Amount</p>
                        <p className="font-semibold">
                          ₹{fund.minimum_sip_invest}
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => dispatch(openInvestmentModal(formPlayload))}
                    className={`block w-full transition-all duration-300 bg-[linear-gradient(91.03deg,#006B66_0%,#009B93_154.27%)] rounded-b-2xl text-white py-3 font-medium shine-button-hover ${idx === 1 ? "relative 2lg:scale-110 2lg:mt-3 2lg:group-hover:scale-[1.11]" : "2lg:group-hover:scale-[1.01]"}`}
                  >
                    Invest In This Fund
                  </button>
                </div>
              );
            })
          ) : (
            <>
              {[...Array(3)].map((_, idx) => (
                <FundCardSkeleton
                  key={idx}
                  isMidCard={idx === 1 ? true : false}
                />
              ))}
            </>
          )}
        </div>

        {/* View All */}
        <div className="mt-10">
          <a
            href={`${ROOT_URL}mutual-funds/${activeTab === "Equity" ? "equity" : activeTab === "Hybrid" ? "hybrid" : "debt"}`}
            className="group w-fit flex items-center justify-center gap-2 font-medium mx-auto border-2 border-primaryGreen text-primaryGreen px-10 py-2 rounded-lg hover:bg-primaryGreen hover:text-white transition"
          >
            View All
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition text-primaryGreen group-hover:text-white"
            >
              <path
                d="M9.61984 4.1176L13.6665 8.16427L9.61984 12.2109"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.33029 8.16406H13.5503"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </section>

      {isTab && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          maxSize="max-w-[80%]"
          useDragToClose
          isBlurOverlay
          className="bg-white rounded-b-none sm:rounded-4xl"
        >
          <RightPart
            className="block lg:hidden"
            isOpen={isOpen}
            calculation={calculation!}
          />
        </CustomModal>
      )}
    </>
  );
}

function RightPart({
  className,
  isOpen,
  calculation,
}: {
  isOpen?: boolean;
  className?: string;
  calculation: CagrResult;
}) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [isChartLoading, setIsChartLoading] = useState(true);

  const chartOptions = {
    chart: {
      type: "pie",
      height: 210,
      backgroundColor: "transparent",
      events: {
        load: () => {
          setIsChartLoading(false);
        },
      },
    },
    title: { text: null },
    tooltip: { pointFormat: "<b>{point.name}</b>: ₹{point.y:,.0f}" },
    plotOptions: {
      pie: {
        innerSize: "60%",
        dataLabels: { enabled: false },
        borderRadius: 8,
        borderColor: "#ffffff",
        borderWidth: 3,
        startAngle: 0,
        endAngle: 360,
      },
    },
    credits: { enabled: false },
    series: [
      {
        name: "Investment",
        colorByPoint: true,
        data: [
          {
            name: "Initial Value Amount",
            y: calculation?.initial,
            color: "#2E84D6",
          },
          { name: "Final Value", y: calculation?.gain, color: "#002C56" },
        ],
      },
    ],
  };

  return (
    <>
      <div
        className={`w-full lg:w-[52%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}
      >
        <div
          className={`h-52.5 ${isChartLoading ? "flex items-center justify-center" : ""}`}
        >
          {isChartLoading && (
            <Image
              src="/next_assets/images/gif/chart_loading.gif"
              unoptimized
              alt="Loading Chart Animation"
              loading="eager"
              width={256}
              height={144}
              priority
              className="w-64 h-36 mx-auto mt-14"
            />
          )}
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={chartOptions}
          />
        </div>

        <div className="bg-white px-0 sm:p-4 rounded-2xl mx-auto sm:w-[90%] space-y-6">
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-medium">
            <p>Initial Investment</p>
            <p className="text-[#2E84D6]">
              {formatIndianNumber(Number(calculation?.initial ?? 0), {
                prefix: "₹",
              })}
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-medium">
            <p>Final Value</p>
            <p>
              {formatIndianNumber(Number(calculation?.final ?? 0), {
                prefix: "₹",
              })}
            </p>
          </div>
          <div className="flex items-center justify-between text-2xs xs:text-2sm sm:text-lg text-[#222222] font-medium">
            <p>CAGR</p>
            <p
              className={
                calculation?.cagr >= 0 ? "text-primaryGreen" : "text-red-500"
              }
            >
              {((calculation?.cagr ?? 0) * 100).toFixed(2)}%
            </p>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 rounded-b-2xl gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a
            href={`${ROOT_URL}start-sip.php`}
            className="w-full block max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-6 xs:px-14 py-2 xs:py-2.5"
          >
            Start STP
          </a>
        </div>
      )}
    </>
  );
}

const PremiumSvgTag = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="22"
    height="32"
    viewBox="0 0 22 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M0 0H22V32L11 27.2L0 32V0Z" fill="#4A21A3" />
    <path
      d="M10.5245 8.46352C10.6741 8.00287 11.3259 8.00287 11.4755 8.46353L12.4593 11.4914C12.5263 11.6974 12.7183 11.8369 12.9349 11.8369H16.1186C16.6029 11.8369 16.8043 12.4567 16.4124 12.7414L13.8368 14.6127C13.6615 14.74 13.5882 14.9657 13.6552 15.1717L14.639 18.1996C14.7886 18.6602 14.2614 19.0433 13.8695 18.7586L11.2939 16.8873C11.1186 16.76 10.8814 16.76 10.7061 16.8873L8.13045 18.7586C7.73859 19.0433 7.21136 18.6602 7.36103 18.1996L8.34484 15.1717C8.41178 14.9657 8.33845 14.74 8.16321 14.6127L5.58755 12.7414C5.1957 12.4567 5.39708 11.8369 5.88145 11.8369H9.06513C9.28174 11.8369 9.47372 11.6974 9.54066 11.4914L10.5245 8.46352Z"
      fill="#FFF3D2"
    />
  </svg>
);

const FundCardSkeleton = ({ isMidCard }: { isMidCard?: boolean }) => {
  return (
    <div
      className={`w-full h-61.25 bg-white border-2 border-[#D9D9D9] rounded-2xl shadow-xs flex flex-col overflow-hidden animate-pulse ${isMidCard && "2lg:scale-110"}`}
    >
      <div className="p-5 flex-1 flex flex-col">
        {/* Header section with logo + title */}
        <div className="flex items-center gap-3 border-b border-[#00000033] pb-5 mb-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full" />
          <div className="flex flex-col gap-2">
            <div className="w-40 h-3 bg-gray-200 rounded-sm" />
            <div className="w-28 h-3 bg-gray-200 rounded-sm" />
          </div>
        </div>

        {/* 3Y SIP Returns */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="w-24 h-3 bg-gray-200 rounded-sm" />
          <div className="w-12 h-3 bg-gray-200 rounded-sm" />
        </div>

        {/* Minimum SIP Amount */}
        <div className="flex items-center justify-between gap-3">
          <div className="w-32 h-3 bg-gray-200 rounded-sm" />
          <div className="w-10 h-3 bg-gray-200 rounded-sm" />
        </div>
      </div>

      {/* Button placeholder */}
      <div className="h-12 bg-gray-200" />
    </div>
  );
};
